import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

const FAQ_CATEGORIES = [
  {
    label: "General",
    questions: [
      {
        q: "What is the Cable Builder?",
        a: "The Cable Builder is an interactive tool to design and order custom cables for audio, video, DMX, and power applications."
      },
      {
        q: "Can I save and share my configuration?",
        a: "Yes, you can save your configuration for later or share it with others."
      }
    ]
  },
  {
    label: "Cable Types",
    questions: [
      {
        q: "What's the difference between Microphone, Instrument, Speaker, etc.?",
        a: "Each cable type is designed for a specific application and has unique compatibility and construction. For example, Microphone cables use XLR connectors, while Speaker cables use Speakon or TS connectors."
      },
      {
        q: "Which cable type should I choose for my application?",
        a: "Choose the cable type that matches your equipment and intended use. Refer to the descriptions in the Cable Type step for guidance."
      }
    ]
  },
  {
    label: "Connectors",
    questions: [
      {
        q: "What do 'brand', 'gender', and 'variant' mean?",
        a: "Brand refers to the manufacturer (Neutrik, Switchcraft, Amphenol). Gender is Male or Female. Variant refers to connector style, such as Standard, Right Angle, or Panel Mount."
      },
      {
        q: "What's the difference between Neutrik, Switchcraft, and Amphenol?",
        a: "These are leading connector brands, each known for quality and reliability. Neutrik is widely used in pro audio, Switchcraft is known for ruggedness, and Amphenol for industrial-grade connectors."
      },
      {
        q: "What is a 'Right Angle' or 'Panel Mount' connector?",
        a: "A Right Angle connector has a 90-degree bend for tight spaces. Panel Mount connectors are designed to be mounted on equipment panels."
      },
      {
        q: "How do I know which connectors are compatible?",
        a: "The builder only shows connectors that are compatible with your selected cable type and brand."
      }
    ]
  },
  {
    label: "Quality",
    questions: [
      {
        q: "What's the difference between Standard, Premium, and Elite?",
        a: "Standard is for everyday use, Premium is more durable for touring, and Elite is top-tier for studio or critical applications."
      }
    ]
  },
  {
    label: "Length",
    questions: [
      {
        q: "What's the maximum/minimum cable length?",
        a: "Lengths typically range from 0.5 ft to 500 ft, depending on cable type and application."
      }
    ]
  },
  {
    label: "Finishing",
    questions: [
      {
        q: "What is TechFlex? What colors are available?",
        a: "TechFlex is a protective sleeving available in various colors to enhance durability and appearance."
      },
              {
          q: "What is Heat Shrink? What are the options?",
          a: "Every cable includes NLFX branding at each connector. Optional add-on heat shrink is slid farther up the cable from the plug (on the jacket), so it does not cover that branding. We offer two 2\" add-ons: clear — plain clear tube, unshrunk, with no printing from us (priced per end), for your own labels or finishing on that section before shrinking; and custom printed — your text or logo on colored heat-shrink with ribbon printing (tube and ribbon colors when available; black tube and white ribbon today). Custom is priced separately per end."
        },
      {
        q: "Can I add custom labels?",
        a: "Yes, you can add standard or custom text labels to your cable."
      }
    ]
  },
  {
    label: "Pricing & Cart",
    questions: [
      {
        q: "How is the price calculated?",
        a: "Price is based on cable type, length, quality, connectors, and finishing options."
      },
      {
        q: "How do I add to cart and checkout?",
        a: "After reviewing your configuration, click Add to Cart and proceed to checkout."
      }
    ]
  },
  {
    label: "Support",
    questions: [
      {
        q: "Who do I contact for help?",
        a: "Use the Contact Support option in the help menu or start a Live Chat."
      },
      {
        q: "Where can I find more technical details?",
        a: "Contact our support team for in-depth technical information."
      }
    ]
  }
];

const LOCAL_STORAGE_KEY = 'nlfx_faq_tab';

export default function FAQDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const questionRefs = useRef([]);

  useEffect(() => {
    if (open) {
      const savedTab = parseInt(localStorage.getItem(LOCAL_STORAGE_KEY), 10);
      if (!isNaN(savedTab) && savedTab >= 0 && savedTab < FAQ_CATEGORIES.length) {
        setTab(savedTab);
      }
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      localStorage.setItem(LOCAL_STORAGE_KEY, tab);
    }
  }, [tab, open]);

  // Search and jump to question
  const handleSearch = (e) => {
    setSearch(e.target.value);
    if (e.target.value.trim().length > 1) {
      for (let catIdx = 0; catIdx < FAQ_CATEGORIES.length; catIdx++) {
        const qIdx = FAQ_CATEGORIES[catIdx].questions.findIndex(q =>
          q.q.toLowerCase().includes(e.target.value.toLowerCase())
        );
        if (qIdx !== -1) {
          setTab(catIdx);
          setTimeout(() => {
            if (questionRefs.current[qIdx]) {
              questionRefs.current[qIdx].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          break;
        }
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
      <DialogTitle>
        Frequently Asked Questions
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search FAQ..."
            value={search}
            onChange={handleSearch}
            variant="outlined"
            size="small"
          />
        </Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {FAQ_CATEGORIES.map((cat, idx) => (
            <Tab key={cat.label} label={cat.label} />
          ))}
        </Tabs>
        <Box>
          <List>
            {FAQ_CATEGORIES[tab].questions.map((q, idx) => (
              <ListItem key={q.q} alignItems="flex-start" ref={el => questionRefs.current[idx] = el}>
                <ListItemText
                  primary={<Typography variant="subtitle1" fontWeight={600}>{q.q}</Typography>}
                  secondary={<Typography variant="body2">{q.a}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
} 